<script>
import axios from "axios"

import DataFlowDiagramTechnology from "@/components/groups/diagram/DataFlowDiagramTechnology.vue"
import DataFlowDiagramTransformation from "@/components/groups/diagram/DataFlowDiagramTransformation.vue"
import DataFlowDiagramEndpoints from "@/components/groups/diagram/DataFlowDiagramEndpoints.vue"
import DataFlowSaveModal from "@/components/groups/diagram/DataFlowSaveModal.vue"
import { mapMutations } from "vuex"
import { client } from "@/main"

const dataFlowInitialState = {
  deviceGroupDesc: "",
  deviceGroupName: "",
  endpoints: [],
  hardwareId: null,
  technology: "",
  transformationId: null
}

export default {
  name: "DataFlowDiagram",
  components: {
    DataFlowSaveModal,
    DataFlowDiagramEndpoints,
    DataFlowDiagramTechnology,
    DataFlowDiagramTransformation
  },
  props: {
    initialDataFlow: {
      type: Object,
      required: false,
      default: null
    },
    isSaveModalOpened: {
      type: Boolean,
      required: true
    },
    readonlyTechnology: {
      type: Boolean,
      required: false,
      default: false
    },
    useOwnSaveBtn: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ["updated"],
  data: () => ({
    dataFlow: JSON.parse(JSON.stringify(dataFlowInitialState)),
    initialDataFlowOrigin: null
  }),
  computed: {
    isDirty() {
      return JSON.stringify(this.initialDataFlowOrigin) !== JSON.stringify(this.dataFlow)
    }
  },
  watch: {
    "dataFlow.technology": {
      handler(technology) {
        this.$emit("technologyChanged", technology)
      }
    },
    initialDataFlow: {
      handler(initialDataFlow) {
        if (initialDataFlow) {
          this.initialDataFlowOrigin = JSON.parse(JSON.stringify(initialDataFlow))
          this.dataFlow = initialDataFlow
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations({
      setError: "modals/setError"
    }),
    async handleUpdateDataFlow() {
      let res

      try {
        res = await client.path("/device-groups/{id}").method("put").create()({
          id: this.dataFlow.deviceGroupId,
          ...this.dataFlow
        })
        if (res.data.errors) {
          this.setError(res.data.errors[0])
        }
      } catch (e) {
        this.setError(e.data.errors[0])
      }

      const nowAssignedEndpointsIds = this.dataFlow.endpoints.map((e) => e.custDestId)
      const originallyAssignedEndpointsIds = this.initialDataFlowOrigin.endpoints.map((e) => e.custDestId)

      const newlyAssignedEndpointsIds = nowAssignedEndpointsIds.filter(
        (eId) => !originallyAssignedEndpointsIds.includes(eId)
      )
      const newlyUnassignedEndpointsIds = originallyAssignedEndpointsIds.filter(
        (eId) => !nowAssignedEndpointsIds.includes(eId)
      )

      // Assign endpoints (one by one...)
      for (const eId of newlyAssignedEndpointsIds) {
        res = await axios.post(
          `${process.env.VUE_APP_API}/device-groups/${this.dataFlow.deviceGroupId}/endpoints/${eId}/assign`
        )
        if (res.data.errors?.messages) {
          this.setError(res.data.errors?.messages)
        }
      }

      // Unassign endpoints (one by one...)
      for (const eId of newlyUnassignedEndpointsIds) {
        res = await axios.delete(
          `${process.env.VUE_APP_API}/device-groups/${this.dataFlow.deviceGroupId}/endpoints/${eId}/assign`
        )
        if (res.data.errors?.messages) {
          this.setError(res.data.errors?.messages)
        }
      }

      this.$emit("updated")
      this.initialDataFlowOrigin = JSON.parse(JSON.stringify(this.dataFlow))
    },
    reset() {
      this.dataFlow = JSON.parse(JSON.stringify(dataFlowInitialState))
    }
  }
}
</script>

<template>
  <div>
    <button
      v-if="isDirty && useOwnSaveBtn"
      class="data-flow-diagram--save-btn btn btn-primary btn-sm align-items-center d-flex primary-shadow"
      @click="handleUpdateDataFlow"
    >
      <span class="atom-icon">
        <img alt="" src="@/assets/icons/actions/ico-btn-ulozit.svg" />
      </span>
      {{ $t("SAVE") }}
    </button>

    <div class="data-flow-diagram w-100">
      <div
        v-if="!dataFlow.transformationId && !dataFlow.hardwareId"
        class="data-flow-diagram__green-line data-flow-diagram__green-line--1-3"
      />
      <div
        v-if="dataFlow.transformationId && !dataFlow.hardwareId"
        class="data-flow-diagram__green-line data-flow-diagram__green-line--1-2"
      />
      <div
        v-if="dataFlow.transformationId && !dataFlow.hardwareId"
        class="data-flow-diagram__green-line data-flow-diagram__green-line--2-3"
      />

      <DataFlowDiagramTechnology
        :data-flow="dataFlow"
        :readonly="readonlyTechnology"
        @change="dataFlow.technology = $event"
      />

      <DataFlowDiagramTransformation
        :data-flow="dataFlow"
        :is-active="!!dataFlow.technology"
        @change="dataFlow.transformationId = $event"
      />

      <!--      <DataFlowDiagramHardware :data-flow="dataFlow" :is-active="!!dataFlow.technologyId" />-->

      <DataFlowDiagramEndpoints
        :data-flow="dataFlow"
        :is-active="!!dataFlow.technology"
        @add="dataFlow.endpoints = dataFlow.endpoints.concat([$event])"
        @remove="dataFlow.endpoints = dataFlow.endpoints.filter((end) => end.custDestId !== $event.custDestId)"
      />

      <DataFlowSaveModal :data-flow="dataFlow" :is-opened="isSaveModalOpened" @close="$emit('closeSave')" />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
